import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from '@auth/auth.service';
import { ClaimsService } from '@app/services/claims.service';
import { CustomerService } from '@app/services/customer.service';
import { getDefaultRedirectUrl } from '@core/services/app-config.service';
import { APP_ROUTES } from '@app/app.routes';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

let knownRoutes: Set<string> | null = null;

function getKnownRoutes(): Set<string> {
	if (!knownRoutes) {
		knownRoutes = new Set(APP_ROUTES.map((r) => r.path).filter((p): p is string => !!p));
	}
	return knownRoutes;
}

@Injectable({
	providedIn: 'root',
})
export class AuthGuard implements CanActivate {
	private readonly authService = inject(AuthService);
	private readonly router = inject(Router);
	private readonly claimsService = inject(ClaimsService);
	private readonly customerService = inject(CustomerService);
	canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
		// Check if we're in the middle of an OAuth callback
		if (route.queryParams['code']) {
			// Return an observable that waits for the authentication to complete
			return this.authService.authenticationEventObservable.pipe(
				first(),
				switchMap((isAuthenticated: boolean) => {
					if (isAuthenticated) {
						// Initialize both data sources after successful authentication
						this.customerService.initializeCustomerData();
						return this.claimsService.ensureUserRegistrationData().pipe(
							map((result: boolean) => {
								if (result) {
									// Restore the intended path saved before the OAuth redirect, if any
									const savedPath = sessionStorage.getItem('post_login_redirect');
									sessionStorage.removeItem('post_login_redirect');
									const defaultPath = new URL(getDefaultRedirectUrl()).pathname;
									const rawPath =
										savedPath && savedPath.startsWith('/') && savedPath !== defaultPath && isKnownRoute(savedPath)
											? savedPath
											: window.location.pathname;
									const [segmentPath, queryString] = rawPath.split('?');
									const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};
									return this.router.createUrlTree([segmentPath], {
										queryParams,
									});
								}
								return false as boolean;
							}),
						);
					}
					return [false as boolean | UrlTree];
				}),
			);
		}

		if (this.authService.isAuthenticated()) {
			// User is already authenticated, initialize customer data and check for registration data
			this.customerService.initializeCustomerData();
			return this.claimsService.ensureUserRegistrationData().pipe(
				map((result: boolean) => {
					if (result) {
						// Check if there's a pending redirect from before the OAuth flow
						const savedPath = sessionStorage.getItem('post_login_redirect');
						if (savedPath && isKnownRoute(savedPath)) {
							sessionStorage.removeItem('post_login_redirect');
							const [segmentPath, queryString] = savedPath.split('?');
							const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};
							return this.router.createUrlTree([segmentPath], { queryParams });
						}
						return true as boolean | UrlTree;
					}
					return false as boolean | UrlTree;
				}),
			);
		} else {
			// Save the intended path so we can restore it after the OAuth callback
			const intendedPath = window.location.pathname + window.location.search;
			const defaultPath = new URL(getDefaultRedirectUrl()).pathname;
			if (intendedPath && intendedPath !== '/' && intendedPath !== defaultPath && isKnownRoute(intendedPath)) {
				sessionStorage.setItem('post_login_redirect', intendedPath);
			}
			void this.authService.login();
			// Return observable that will resolve when authentication is complete
			return this.authService.authenticationEventObservable.pipe(
				first(),
				switchMap((isAuthenticated: boolean) => {
					if (isAuthenticated) {
						// Initialize both data sources after successful authentication
						this.customerService.initializeCustomerData();
						return this.claimsService.ensureUserRegistrationData().pipe(
							map((result: boolean) => {
								if (result) {
									const savedPath = sessionStorage.getItem('post_login_redirect');
									if (savedPath && isKnownRoute(savedPath)) {
										sessionStorage.removeItem('post_login_redirect');
										const [segmentPath, queryString] = savedPath.split('?');
										const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};
										return this.router.createUrlTree([segmentPath], {
											queryParams,
										});
									}
									return true as boolean | UrlTree;
								}
								return false as boolean | UrlTree;
							}),
						);
					} else {
						return [false];
					}
				}),
			);
		}
	}
}

function isKnownRoute(path: string): boolean {
	const firstSegment = path.split('?')[0].split('/').filter(Boolean)[0];
	return !!firstSegment && getKnownRoutes().has(firstSegment);
}
